import SEO from "@/components/SEO";
import { useState } from "react";
import { webPageSchema, donateActionSchema, breadcrumbSchema } from "@/lib/organization-schema";
import { Heart, Building, Handshake, Building2, Megaphone } from "lucide-react";
import { NativeSelect } from "@/components/ui/native-select";

const GIVE_URL = "https://give.abbashouse.org";

const waysToHelp = [
  {
    Icon: Heart,
    title: "Monthly Giving",
    desc: "Just $38/month will make you part of a team housing real people once our center opens. 20 monthly donors = 1 person fully housed.",
  },
  {
    Icon: Building,
    title: "One-Time Gift",
    desc: "Help us reach our long-term $3M goal to open our first Community Housing Center. Every one-time gift moves us closer to making this vision a reality.",
  },
  {
    Icon: Handshake,
    title: "Volunteer",
    desc: "Once our center opens, volunteers will help with mentorship, job coaching, life skills, and more. Sign up now to be among the first.",
  },
  {
    Icon: Building2,
    title: "Corporate Partnership",
    desc: "Align your brand with compassion as we build toward our first facility. Sponsorships, matching gifts, and in-kind donations are all welcome.",
  },
  {
    Icon: Megaphone,
    title: "Spread the Word",
    desc: "Share our mission on social media, in your church, or with your network. Awareness will help us reach our goal and save lives.",
  },
];

export default function PartnerWithUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    helpType: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <main>
      <SEO
        title="Partner With Us"
        description="Partner with Abba's House Communities through monthly giving, one-time gifts, volunteering, or corporate sponsorship. $38/month houses one person — be part of the solution."
        canonical="https://abbashouse.org/partner"
        jsonLd={[
          webPageSchema({ name: "Partner With Us — Abba's House Communities", description: "Partner with Abba's House Communities through giving, volunteering, or corporate sponsorship.", url: "https://abbashouse.org/partner" }),
          donateActionSchema(),
          breadcrumbSchema([
            { name: "Home", url: "https://abbashouse.org/" },
            { name: "Partner With Us", url: "https://abbashouse.org/partner" },
          ]),
        ]}
      />
      <section className="bg-primary text-primary-foreground py-24 md:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <p className="text-sm font-semibold tracking-[0.3em] uppercase opacity-50 mb-6">Partner With Us</p>
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight leading-none mb-8 max-w-3xl">
            Be Part of the Solution.
          </h1>
          <p className="text-lg sm:text-xl opacity-70 leading-relaxed max-w-2xl">
            Whether you give, volunteer, or spread the word — there is a place for you in this mission. Together, we will make sure no one sleeps without a home.
          </p>
        </div>
      </section>

      {/* Impact Numbers */}
      <section className="py-12 md:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            {[
              { num: "$38/mo", label: "× 20 donors = 1 person housed once we open" },
              { num: "$750/mo", label: "Will fully house, feed & restore 1 life" },
              { num: "$3M", label: "Goal to open our first facility" },
            ].map((item) => (
              <div key={item.num} className="border border-border rounded-2xl p-10 hover:border-foreground transition-colors">
                <div className="text-5xl font-black mb-3">{item.num}</div>
                <p className="text-muted-foreground text-sm leading-snug">{item.label}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <a
              href={GIVE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-10 py-5 rounded-full bg-primary text-primary-foreground text-lg font-bold tracking-wide hover:opacity-80 transition-opacity"
            >
              Give Now
            </a>
          </div>
        </div>
      </section>

      {/* Ways to Get Involved */}
      <section className="py-20 md:py-28 bg-secondary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Ways to Get Involved</h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              There's a place for everyone in this mission. Find yours below.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {waysToHelp.map((way) => (
              <div key={way.title} className="bg-background rounded-2xl p-8 flex flex-col gap-4 hover:shadow-md transition-shadow">
                <way.Icon className="w-7 h-7 text-primary" strokeWidth={1.5} />
                <h3 className="text-xl font-bold">{way.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{way.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-2xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Get In Touch</h2>
            <p className="text-muted-foreground text-lg">
              Tell us how you'd like to help and we'll be in touch.
            </p>
          </div>

          {submitted ? (
          <div className="bg-primary text-primary-foreground rounded-2xl p-12 text-center">
              <h3 className="text-2xl font-bold mb-4">Thank You!</h3>
              <p className="opacity-70 leading-relaxed">
                We've received your message and we'll be in touch soon. Your willingness to help means everything.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <label htmlFor="name" className="text-sm font-semibold tracking-wide">
                  Full Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-foreground transition-colors"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="text-sm font-semibold tracking-wide">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-foreground transition-colors"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="helpType" className="text-sm font-semibold tracking-wide">
                  How Would You Like to Help?
                </label>
                <NativeSelect
                  id="helpType"
                  name="helpType"
                  required
                  value={formData.helpType}
                  onChange={handleChange}
                >
                  <option value="">Select an option...</option>
                  <option value="Donate">Donate</option>
                  <option value="Volunteer">Volunteer</option>
                  <option value="Corporate Partner">Corporate Partner</option>
                  <option value="Other">Other</option>
                </NativeSelect>
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="message" className="text-sm font-semibold tracking-wide">
                  Message <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us more about how you'd like to get involved..."
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-foreground transition-colors resize-none"
                />
              </div>
              <button
                type="submit"
                className="w-full py-4 rounded-full bg-primary text-primary-foreground text-base font-bold tracking-wide hover:opacity-80 transition-opacity"
              >
                Send Message
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Give CTA */}
      <section className="bg-primary text-primary-foreground py-12 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight mb-6">
            Ready to Make a Difference?
          </h2>
          <p className="text-lg opacity-70 mb-10 max-w-2xl mx-auto">
            Every gift — $38 or $750 or more — moves us one step closer to opening our first facility and changing hundreds of lives.
          </p>
          <a
            href={GIVE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-10 py-5 rounded-full bg-primary-foreground text-primary text-lg font-bold tracking-wide hover:opacity-90 transition-opacity"
          >
            Give Now ↗
          </a>
        </div>
      </section>
    </main>
  );
}